/**
 * Normalize text for search by removing diacritics and converting to lowercase
 * Handles accented characters (e.g., Yorùbá → yoruba)
 *
 * @param text - The text to normalize
 * @returns Normalized text suitable for case-insensitive, diacritic-insensitive search
 *
 * @example
 * normalizeSearchText('Yorùbá') // returns 'yoruba'
 * normalizeSearchText('Français') // returns 'francais'
 */
export function normalizeSearchText(text: string): string {
  if (!text) return '';

  return text
    .normalize('NFD') // Decompose combined characters (e.g., é → e + ´)
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .toLowerCase()
    .trim();
}

/**
 * Check if a search query matches text (case-insensitive, diacritic-insensitive)
 *
 * @param text - The text to search in
 * @param query - The search query
 * @returns True if query matches text
 *
 * @example
 * searchMatches('Yorùbá Bible', 'yoruba') // returns true
 * searchMatches('King James Version', 'kjv') // returns false
 */
export function searchMatches(text: string, query: string): boolean {
  if (!query.trim()) return true;
  return normalizeSearchText(text).includes(normalizeSearchText(query));
}

/**
 * Filter an array of items by a search query on specified fields
 *
 * @param items - Array of items to filter
 * @param query - Search query
 * @param fields - Fields to search in (can be nested using dot notation)
 * @returns Filtered array
 *
 * @example
 * const versions = [
 *   { code: 'KJV', title: 'King James Version' },
 *   { code: 'YOR', title: 'Bíbélì Mímọ́ Yorùbá' }
 * ];
 * filterBySearch(versions, 'yoruba', ['code', 'title'])
 * // returns [{ code: 'YOR', title: 'Bíbélì Mímọ́ Yorùbá' }]
 */
type SearchablePrimitive = string | number | boolean | null | undefined;

type SearchableValue = SearchablePrimitive | SearchableRecord;

interface SearchableRecord {
  [key: string]: SearchableValue;
}

export function filterBySearch<T extends SearchableRecord>(
  items: T[],
  query: string,
  fields: string[]
): T[] {
  if (!query.trim()) return items;

  const normalizedQuery = normalizeSearchText(query);

  return items.filter((item) => {
    return fields.some((field) => {
      const value = getNestedValue(item, field);
      if (value == null) return false;
      return normalizeSearchText(String(value)).includes(normalizedQuery);
    });
  });
}

/**
 * Get nested object value using dot notation
 * @private
 */
function getNestedValue(
  obj: SearchableRecord,
  path: string
): SearchablePrimitive {
  const keys = path.split('.');
  let current: SearchableValue | null = obj;

  for (const key of keys) {
    if (!isSearchableRecord(current)) {
      return null;
    }
    const nextValue: SearchableValue = current[key];
    current = nextValue ?? null;
  }

  return isSearchableRecord(current) ? null : current ?? null;
}

function isSearchableRecord(
  value: SearchableValue | null
): value is SearchableRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
